/**
 * Shared template for /business/compare/* pages.
 * Sections: Hero · Feature Table · Metrics · Deep-Dive · Summary Cards · FAQ · CTA
 */
import { useState } from 'react';
import { Check, X, Minus, ChevronDown, ArrowRight, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

// ─── Types ────────────────────────────────────────────────────────────────────
export type CellValue = 'yes' | 'no' | 'partial' | string;

export type CompareContent = {
  competitor: string;
  competitorSegment: string;
  tagline: string;
  intro: string;
  tableGroups: {
    groupName: string;
    rows: { feature: string; ours: CellValue; theirs: CellValue }[];
  }[];
  metrics: { value: string; label: string; sub: string }[];
  deepDive: { title: string; body: string; highlight?: string }[];
  ourAdvantages: string[];
  theirAdvantages: string[];
  faqs: { q: string; a: string }[];
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function FadeUp({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Cell({ val, highlight }: { val: CellValue; highlight?: boolean }) {
  if (val === 'yes')
    return (
      <span
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${highlight ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-50 text-emerald-500'}`}
      >
        <Check className="h-4 w-4" strokeWidth={2.5} aria-label="Yes" />
      </span>
    );
  if (val === 'no')
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-400">
        <X className="h-4 w-4" strokeWidth={2.5} aria-label="No" />
      </span>
    );
  if (val === 'partial')
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-50 text-amber-500">
        <Minus className="h-3.5 w-3.5" strokeWidth={2.5} aria-label="Partial" />
      </span>
    );
  return (
    <span
      className={`inline-block rounded-md px-2 py-0.5 text-[12px] font-semibold ${
        highlight
          ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
          : 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300'
      }`}
    >
      {val}
    </span>
  );
}

function AccordionItem({
  q,
  a,
  open,
  onToggle,
}: {
  q: string;
  a: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-100 dark:border-zinc-800">
      <button
        className="flex w-full items-center justify-between gap-4 py-4 text-left text-base font-semibold text-gray-900 transition-colors hover:text-violet-700 dark:text-zinc-100 dark:hover:text-violet-400"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>{q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="pb-5 pr-8 text-[15px] leading-relaxed text-gray-600 dark:text-zinc-400">
          {a}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function ComparePageTemplate({ content }: { content: CompareContent }) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <B2BMarketingPageLayout>

      {/* ══════════════════════════════════════════════════════════════════════
          1. HERO
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="border-b border-gray-100 bg-white px-4 pb-14 pt-12 dark:border-zinc-800 dark:bg-zinc-950 sm:px-6 md:px-8">
        <FadeUp className="mx-auto max-w-3xl text-center">
          {/* Breadcrumb */}
          <div className="mb-4 flex items-center justify-center gap-2 text-[12px] font-medium text-gray-400 dark:text-zinc-500">
            <span>Compare</span>
            <span>›</span>
            <span className="text-violet-600 dark:text-violet-400">
              GoGrabJob vs {content.competitor}
            </span>
          </div>

          {/* Segment badge */}
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[12px] font-medium text-gray-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
            <ShieldCheck className="h-3.5 w-3.5 text-violet-500" />
            {content.competitor} is a {content.competitorSegment}
          </span>

          {/* Headline */}
          <h1 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl">
            Why Choose{' '}
            <span className="text-violet-600 dark:text-violet-400">GoGrabJob Business</span>
            <br className="hidden sm:block" /> Over {content.competitor}?
          </h1>

          {/* Tagline */}
          <p className="mt-4 text-lg font-medium text-gray-700 dark:text-zinc-300">
            {content.tagline}
          </p>

          {/* Intro */}
          <p className="mt-3 text-base leading-relaxed text-gray-500 dark:text-zinc-400">
            {content.intro}
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="/business/register"
              className="inline-flex items-center gap-2 rounded-xl bg-[#8b80f9] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-200/50 transition-colors hover:bg-[#7c72f0]"
            >
              Start for free
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/business/demo"
              className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-white px-6 py-3 text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-50 dark:border-violet-700 dark:bg-transparent dark:text-violet-400 dark:hover:bg-violet-950/30"
            >
              Book a Demo
            </a>
          </div>
        </FadeUp>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          2. COMPARISON TABLE
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="px-4 py-14 sm:px-6 md:px-8">
        <FadeUp className="mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-500">
              Feature comparison
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
              Head-to-Head: GoGrabJob vs {content.competitor}
            </h2>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_140px_140px] border-b-2 border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800/80">
              <div className="px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                Feature
              </div>
              <div className="border-l-2 border-gray-200 px-4 py-4 text-center dark:border-zinc-700">
                <span className="block text-[11px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">
                  GoGrabJob
                </span>
                <span className="mt-0.5 block text-[10px] text-violet-400/70 dark:text-violet-600">
                  Business
                </span>
              </div>
              <div className="border-l-2 border-gray-200 px-4 py-4 text-center dark:border-zinc-700">
                <span className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
                  {content.competitor}
                </span>
              </div>
            </div>

            {/* Grouped rows */}
            {content.tableGroups.map((group) => (
              <div key={group.groupName}>
                {/* Group header */}
                <div className="grid grid-cols-[1fr_140px_140px] bg-violet-50/70 dark:bg-violet-950/20">
                  <div
                    className="col-span-3 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-violet-600 dark:text-violet-400"
                  >
                    {group.groupName}
                  </div>
                </div>

                {/* Data rows */}
                {group.rows.map((row, i) => (
                  <div
                    key={row.feature}
                    className={`grid grid-cols-[1fr_140px_140px] border-t border-gray-100 dark:border-zinc-800/60 ${
                      i % 2 === 1 ? 'bg-gray-50/50 dark:bg-zinc-900/40' : ''
                    }`}
                  >
                    <div className="flex items-center px-5 py-3.5 text-sm text-gray-700 dark:text-zinc-300">
                      {row.feature}
                    </div>
                    <div className="flex items-center justify-center border-l border-gray-100 px-4 py-3.5 dark:border-zinc-800">
                      <Cell val={row.ours} highlight />
                    </div>
                    <div className="flex items-center justify-center border-l border-gray-100 px-4 py-3.5 dark:border-zinc-800">
                      <Cell val={row.theirs} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-gray-400 dark:text-zinc-500">
            <span className="flex items-center gap-1">
              <Check className="h-3 w-3 text-emerald-500" /> Available
            </span>
            <span className="flex items-center gap-1">
              <Minus className="h-3 w-3 text-amber-500" /> Partial / Add-on
            </span>
            <span className="flex items-center gap-1">
              <X className="h-3 w-3 text-red-400" /> Not available
            </span>
            <span className="ml-auto">Based on publicly available information. {new Date().getFullYear()}.</span>
          </div>
        </FadeUp>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          3. PERFORMANCE METRICS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="px-4 py-4 sm:px-6 md:px-8">
        <FadeUp>
          <div className="mx-auto max-w-4xl">
            <div
              className="overflow-hidden rounded-2xl px-6 py-10 text-center"
              style={{ background: 'linear-gradient(135deg,#7B6EE8 0%,#8E86FF 55%,#b0a9ff 100%)' }}
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
                Measurable outcomes
              </p>
              <h2 className="mt-2 text-xl font-bold text-white sm:text-2xl">
                GoGrabJob Business delivers better results
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-px sm:grid-cols-3">
                {content.metrics.map((m, i) => (
                  <div
                    key={m.label}
                    className={`flex flex-col items-center px-6 py-4 ${
                      i < content.metrics.length - 1
                        ? 'border-b border-white/20 sm:border-b-0 sm:border-r'
                        : ''
                    }`}
                  >
                    <span className="text-[3rem] font-extrabold leading-none tabular-nums text-white">
                      {m.value}
                    </span>
                    <span className="mt-1 text-[13px] font-semibold text-white/90">{m.label}</span>
                    <span className="mt-0.5 text-[11px] text-white/55">{m.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          4. DEEP-DIVE ANALYSIS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="px-4 py-14 sm:px-6 md:px-8">
        <div className="mx-auto max-w-4xl">
          <FadeUp className="mb-10 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-500">
              Deep-dive analysis
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
              Why recruiters switch from {content.competitor} to GoGrabJob
            </h2>
          </FadeUp>

          <div className="space-y-6">
            {content.deepDive.map((item, i) => (
              <FadeUp key={item.title} delay={i * 0.06}>
                <div className={`rounded-2xl border p-7 ${
                  item.highlight
                    ? 'border-violet-100 bg-violet-50/40 dark:border-violet-800/30 dark:bg-violet-950/15'
                    : 'border-gray-100 bg-white dark:border-zinc-800 dark:bg-zinc-900/50'
                }`}>
                  <div className={`flex flex-col gap-5 ${item.highlight ? 'md:flex-row md:items-start' : ''}`}>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-[15px] leading-[1.75] text-gray-600 dark:text-zinc-400">
                        {item.body}
                      </p>
                    </div>
                    {item.highlight && (
                      <div className="shrink-0 rounded-xl border border-violet-200 bg-white p-5 shadow-sm dark:border-violet-800/40 dark:bg-zinc-900 md:w-60">
                        <p className="text-[13px] font-semibold uppercase tracking-wide text-violet-500">
                          Key advantage
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-zinc-300">
                          {item.highlight}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          5. SUMMARY — WHERE EACH LEADS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="border-t border-gray-100 px-4 py-14 dark:border-zinc-800 sm:px-6 md:px-8">
        <FadeUp className="mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-2xl">
              The honest breakdown
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {/* GoGrabJob wins */}
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 dark:border-emerald-800/30 dark:bg-emerald-950/15">
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <h3 className="text-sm font-bold text-emerald-800 dark:text-emerald-300">
                  Where GoGrabJob leads
                </h3>
              </div>
              <ul className="space-y-2.5">
                {content.ourAdvantages.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-[14px] text-gray-700 dark:text-zinc-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                    {a}
                  </li>
                ))}
              </ul>
            </div>

            {/* Competitor leads */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-6 dark:border-zinc-700 dark:bg-zinc-800/30">
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-300 text-white dark:bg-zinc-600">
                  <Minus className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <h3 className="text-sm font-bold text-gray-600 dark:text-zinc-400">
                  Where {content.competitor} still leads
                </h3>
              </div>
              <ul className="space-y-2.5">
                {content.theirAdvantages.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-[14px] text-gray-500 dark:text-zinc-400">
                    <Minus className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          6. FAQ
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="border-t border-gray-100 px-4 py-14 dark:border-zinc-800 sm:px-6 md:px-8">
        <FadeUp className="mx-auto max-w-2xl">
          <div className="mb-8 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-500">
              FAQ
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
              Common questions about switching
            </h2>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-zinc-800">
            {content.faqs.map((faq, i) => (
              <AccordionItem
                key={faq.q}
                q={faq.q}
                a={faq.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </FadeUp>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          7. CLOSING CTA
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="px-4 pb-16 pt-4 sm:px-6 md:px-8">
        <FadeUp>
          <div
            className="mx-auto max-w-4xl overflow-hidden rounded-3xl p-10 text-center"
            style={{ background: 'linear-gradient(135deg,#7B6EE8 0%,#8E86FF 55%,#b0a9ff 100%)' }}
          >
            <h2 className="text-2xl font-bold text-white sm:text-3xl">
              Ready to move on from {content.competitor}?
            </h2>
            <p className="mx-auto mt-3 max-w-md text-[15px] text-white/80">
              Set up GoGrabJob Business in 48 hours. No credit card required to start.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="/business/register"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-white bg-white px-7 py-3 text-sm font-bold text-[#8b80f9] shadow-md transition-all hover:bg-white/95"
              >
                Start for free
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/business/demo"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-white/40 px-7 py-3 text-sm font-semibold text-white transition-all hover:border-white hover:bg-white/10"
              >
                Book a 30-min demo
              </a>
            </div>
            <Link
              to="/business"
              className="mt-5 block text-xs text-white/50 hover:text-white/80 transition-colors"
            >
              ← Back to GoGrabJob Business
            </Link>
          </div>
        </FadeUp>
      </section>

    </B2BMarketingPageLayout>
  );
}
